import { EXECUTION_STATES, parseExecutionState, type ExecutionState } from './executionState';

/** The colour a honeycomb cluster shows once its children are summarised. */
export type TrafficLightTier = 'red' | 'yellow' | 'green';

/** Only these four states appear in the honeycomb; the rest are terminal or idle. */
export const HONEYCOMB_TIER_BY_STATE = {
  unassigned: 'red',
  stalled: 'yellow',
  expired: 'yellow',
  active: 'green',
} as const satisfies Partial<Record<ExecutionState, TrafficLightTier>>;

export type HoneycombEligibleState = keyof typeof HONEYCOMB_TIER_BY_STATE;

export interface ExecutionStateSpec {
  state: ExecutionState;
  /**
   * How much attention the state demands. The child with the highest severity lends its
   * colour to the parent cluster. Unrelated to `sortPriority`.
   */
  severity: number;
  /**
   * Which execution stands for a group when a list shows one row per group. Lower comes
   * first. Unrelated to `severity` — the two orders disagree on purpose.
   */
  sortPriority: number;
  /** antd tag colour. */
  tagColor: string;
  labelId: string;
  trafficLightTier: TrafficLightTier | null;
}

/**
 * `sortPriority` starts at 2 because 1 belonged to the removed `delayed` state. The gap is
 * kept so this catalog orders lists exactly as `PRIORITY_BY_STATUS` did before it.
 */
const SPECS: Record<ExecutionState, Omit<ExecutionStateSpec, 'state' | 'labelId' | 'trafficLightTier'>> = {
  unassigned: { severity: 70, sortPriority: 2, tagColor: 'magenta' },
  inactive: { severity: 50, sortPriority: 9, tagColor: 'default' },
  active: { severity: 60, sortPriority: 3, tagColor: 'blue' },
  stalled: { severity: 120, sortPriority: 11, tagColor: 'purple' },
  completed: { severity: 20, sortPriority: 4, tagColor: 'green' },
  expired: { severity: 100, sortPriority: 8, tagColor: 'orange' },
  canceled: { severity: 30, sortPriority: 10, tagColor: 'default' },
  complete_expired: { severity: 40, sortPriority: 5, tagColor: 'orange' },
  incomplete: { severity: 90, sortPriority: 6, tagColor: 'red' },
  incomplete_partial: { severity: 80, sortPriority: 7, tagColor: 'red' },
};

export const EXECUTION_STATE_CATALOG: Record<ExecutionState, ExecutionStateSpec> =
  Object.fromEntries(
    EXECUTION_STATES.map(state => [
      state,
      {
        ...SPECS[state],
        state,
        labelId: `work.order.status.${state}`,
        trafficLightTier:
          state in HONEYCOMB_TIER_BY_STATE
            ? HONEYCOMB_TIER_BY_STATE[state as HoneycombEligibleState]
            : null,
      },
    ]),
  ) as Record<ExecutionState, ExecutionStateSpec>;

export const getExecutionStateSpec = (state: ExecutionState): ExecutionStateSpec =>
  EXECUTION_STATE_CATALOG[state];

export const isHoneycombEligibleState = (
  state: ExecutionState,
): state is HoneycombEligibleState => state in HONEYCOMB_TIER_BY_STATE;

/**
 * Labels for the two cases that are not execution states but still have to render. Kept
 * here so surfaces stop inventing their own fallback copy.
 */
export const EXECUTION_STATE_FALLBACK_LABEL_IDS = {
  /** `currentState` has not been written yet — the state is still arriving. */
  none: 'work.order.status.loading',
  /** `currentState` holds a value this app does not recognise. */
  unknown: 'work.order.status.unknown',
} as const;

/**
 * The label for anything a surface may hold: a known state, nothing yet, or a value the
 * app does not recognise. Pass the raw `currentState` so the last two can be told apart.
 */
export const getExecutionStateLabelId = (value: unknown): string => {
  const state = parseExecutionState(value);

  if (state) {
    return getExecutionStateSpec(state).labelId;
  }

  return value == null || value === ''
    ? EXECUTION_STATE_FALLBACK_LABEL_IDS.none
    : EXECUTION_STATE_FALLBACK_LABEL_IDS.unknown;
};

/** List ordering for a raw `currentState`. Unrecognised states sort last explicitly. */
export const getExecutionSortPriority = (value: unknown): number => {
  const state = parseExecutionState(value);
  return state ? getExecutionStateSpec(state).sortPriority : Number.MAX_SAFE_INTEGER;
};
